import React, { useMemo } from "react";

import ComparePlayersChartView, {
  ComparePlayersChartProps,
  ComparePlayersChartViewProps,
} from "./ComparePlayersChartView";

/**
 * Calculate a nice interval for Y-axis ticks based on data range
 * Produces approximately 5-10 ticks with clean values (1, 2, 5, 10, 20, etc.)
 * @param range - The data range (max - min)
 * @returns A nice interval value
 */
const calculateNiceInterval = (range: number): number => {
  const TARGET_TICKS = 6;
  const roughInterval = range / TARGET_TICKS;

  // Find the magnitude (power of 10)
  const magnitude = Math.pow(10, Math.floor(Math.log10(roughInterval)));

  // Normalize to 1-10 range
  const normalized = roughInterval / magnitude;

  // Round to nice values: 1, 2, 5, or 10
  const niceNormalized =
    normalized <= 1 ? 1 : normalized <= 2 ? 2 : normalized <= 5 ? 5 : 10;

  return niceNormalized * magnitude;
};

/**
 * Container component for ComparePlayersChart
 * Handles Y-axis domain and tick calculations for the chart
 * @param props - Chart configuration props
 * @returns Rendered ComparePlayersChartView with calculated Y-axis values
 */
const ComparePlayersChartContainer: React.FC<
  ComparePlayersChartProps
> = props => {
  const { data, yKeys } = props;

  const { yDomainMin, yTickValues } = useMemo(() => {
    const allYValues = data.flatMap(d =>
      yKeys
        .map(key => {
          const value = d[key];
          return typeof value === "number" && Number.isFinite(value)
            ? value
            : null;
        })
        .filter((val): val is number => val !== null)
    );

    if (allYValues.length === 0) {
      return { yDomainMin: 0, yTickValues: undefined };
    }

    const minY = Math.min(...allYValues);
    const maxY = Math.max(...allYValues);
    const range = maxY - minY;
    const yPadding = range > 0 ? range * 0.1 : Math.abs(maxY) * 0.1 || 1;

    const yDomainMin = minY - yPadding;
    const yDomainMax = maxY + yPadding;
    const includesZero = yDomainMin < 0 && yDomainMax > 0;

    if (!includesZero) {
      return { yDomainMin, yTickValues: undefined };
    }

    // Calculate dynamic interval based on data range to avoid too many ticks
    const totalRange = yDomainMax - yDomainMin;
    const niceInterval = calculateNiceInterval(totalRange);

    const numPositiveTicks = Math.floor(yDomainMax / niceInterval);
    const numNegativeTicks = Math.floor(Math.abs(yDomainMin) / niceInterval);

    const positiveTicks = Array.from({ length: numPositiveTicks + 1 }, (_, i) =>
      Number((i * niceInterval).toFixed(1))
    );

    const negativeTicks = Array.from({ length: numNegativeTicks }, (_, i) =>
      Number((-(i + 1) * niceInterval).toFixed(1))
    ).reverse();

    const yTickValues = [...negativeTicks, ...positiveTicks];

    return { yDomainMin, yTickValues };
  }, [data, yKeys]);

  const viewProps: ComparePlayersChartViewProps = useMemo(
    () => ({
      ...props,
      yDomainMin,
      yTickValues,
    }),
    [props, yDomainMin, yTickValues]
  );

  return <ComparePlayersChartView {...viewProps} />;
};

export default ComparePlayersChartContainer;
